import { makeStyles, Theme } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { IHeaderParams, RowNode } from 'ag-grid-community';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { CONSTANTS } from '../../../../../core/config/constants';
import {
  useTableGridApiDispatch,
  useTableGridApiState,
} from '../../../TableGridApiContext/TableGridApiContext';
import { TableGridApiAction } from '../../../TableGridApiContext/TableGridApiContext.types';
import { getNodeIdGetter } from './selectAllRenderer.utils';

const HeaderSelectAllRenderer: React.FC<IHeaderParams> = ({ api }) => {
  const classes = useStyles();
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const { selectedRows, rowSelectionIdGetter, rowsAndIdsOrder } = useTableGridApiState();
  const dispatchTableApi = useTableGridApiDispatch();
  const getNodeId = getNodeIdGetter(rowSelectionIdGetter);

  const onSelectionChanged = useCallback(() => {
    let isAllSelected = true;
    let isIndeterminate = false;

    const setFlags = (node: RowNode) => {
      if (!node.data || selectedRows.indexOf(getNodeId(node)) === -1) {
        isAllSelected = false;
      } else {
        isIndeterminate = true;
      }
    };

    if (api.getModel().getType() === 'clientSide' && !_.isEmpty(api.getFilterModel())) {
      // forEachNodeAfterFilterAndSort function only works on clientSide row model
      api.forEachNodeAfterFilterAndSort((node: RowNode) => {
        setFlags(node);
      });
    } else {
      api.forEachNode((node: RowNode) => {
        setFlags(node);
      });
    }

    setIsAllSelected(api.getDisplayedRowCount() > 0 ? isAllSelected : false);
    setIsIndeterminate(isIndeterminate && !isAllSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  const handleClick = () => {
    if (
      api.getModel().constructor.name === CONSTANTS.SERVER_SIDE_MODEL &&
      (api.getModel() as any).isLoading()
    ) {
      return;
    }

    const orderIds: any = rowsAndIdsOrder;

    const ids: string[] = [];

    const processNode = (node: any) => {
      if (!node.data) return;

      const id = getNodeId(node);

      if (isAllSelected || selectedRows.indexOf(id) === -1) {
        ids.push(id);
      }

      if (orderIds && id !== orderIds.id) {
        orderIds[id] = node.id;
      }
    };

    if (api.getModel().getType() === 'clientSide' && !_.isEmpty(api.getFilterModel())) {
      // forEachNodeAfterFilterAndSort function only works on clientSide row model
      api.forEachNodeAfterFilterAndSort((node: any) => {
        processNode(node);
      });
    } else {
      api.forEachNode((node: any) => {
        processNode(node);
      });
    }

    dispatchTableApi({
      type: TableGridApiAction.AddRowsAndIdsOrder,
      payload: orderIds,
    });

    dispatchTableApi({
      type: !isAllSelected
        ? TableGridApiAction.AddSelectedRows
        : TableGridApiAction.RemoveSelectedRows,
      payload: ids,
    });
  };

  useEffect(() => {
    onSelectionChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  useEffect(() => {
    api.addEventListener('paginationChanged', onSelectionChanged);

    return () => {
      api.removeEventListener('paginationChanged', onSelectionChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  return (
    <Checkbox
      checked={isAllSelected}
      onChange={handleClick}
      indeterminate={isIndeterminate}
      color="primary"
      classes={classes}
      inputProps={{ 'aria-label': 'select-all' }}
    />
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginLeft: '-4px',
    padding: 0,
    color: theme.palette.secondary.dark,

    '&$checked': {
      color: theme.palette.secondary.dark,
    },
  },
  checked: {},
}));

export default HeaderSelectAllRenderer;
